// frontend/src/components/ChatWidget.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE } from 'config';

const DEFAULTS = {
  apiBase: API_BASE,
  tenantId: 'scalabledeploy-site',
  assistantType: 'scalabledeploy-site',
  namespace: 'scalabledeploy-site-v2',
  ui: {
    bubbleLabel: 'Ask ScalableDeploy',
    bubbleSubLabel: 'Plans, trial, platform fit',
    headerKicker: 'ScalableDeploy',
    headerTitle: 'Sales & Trial Assistant',
    placeholder: 'Ask about pricing, the trial, setup, or platform fit…',
    welcomeMessage:
      'Hi — I can explain ScalableDeploy pricing, the 7-day trial, setup, and platform fit. Ask about plans or whether your store is a fit.',
    footerText: 'Powered by ScalableDeploy · Trial-first assistant',
    enableNudge: true,
    nudgeText: 'Questions about pricing, the 7-day trial, or whether your store is a fit? Ask here.',
    nudgeDelayMs: 9000,
    nudgeOncePerSession: true,
    nudgeOnScrollPercent: 35,
  },
};

function getGlobalWidgetConfig() {
  try {
    if (typeof window === 'undefined') return {};
    return window.ScalableDeployWidget || {};
  } catch {
    return {};
  }
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function isInternalTenant(tenantId) {
  return tenantId === 'scalabledeploy-site';
}

function getInternalSourceHref(rawUrl) {
  if (!rawUrl || typeof window === 'undefined') return null;
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const allowedHosts = new Set(['scalabledeploy.com', 'www.scalabledeploy.com']);
    const allowedPaths = new Set(['/', '/trial', '/demo/rag']);
    if (parsed.protocol !== 'https:' || !allowedHosts.has(parsed.hostname) || !allowedPaths.has(parsed.pathname)) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

function getSessionId(tenantId) {
  try {
    const key = `sd_widget_sid_${tenantId}`;
    const existing = localStorage.getItem(key);
    if (existing) return existing;

    const sid = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, sid);
    return sid;
  } catch {
    return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function getPagePath() {
  try {
    if (typeof window === 'undefined') return '';
    return window.location?.pathname || '';
  } catch {
    return '';
  }
}

function looksLikeOrderQuery(text) {
  return /\b(order|tracking|where\s+is\s+my|order\s+status|shipping|when\s+will\s+i\s+get|tracking\s+number)\b/i.test(text);
}

function OrderLookup({ apiBase, onResult, onNoMatch, emitEvent }) {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  async function lookup(params) {
    setLoading(true);
    setMsg('');

    try {
      const qs = new URLSearchParams(params).toString();
      const url = `${(apiBase || '').replace(/\/$/, '')}/shopify/lookup?${qs}`;
      const res = await fetch(url, { method: 'GET' });
      const json = await res.json();

      if (res.ok && json.found) {
        emitEvent && emitEvent('order_lookup_success', { method: Object.keys(params)[0] });
        onResult && onResult(json);
      } else {
        emitEvent && emitEvent('order_lookup_no_match', { method: Object.keys(params)[0] });
        onNoMatch && onNoMatch(json);
      }
    } catch (err) {
      console.error('order lookup failed', err);
      setMsg('Lookup failed — try again or continue to chat.');
      emitEvent && emitEvent('order_lookup_error', { error: err.message });
      onNoMatch && onNoMatch({ error: err.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="order-lookup p-3 bg-gray-50 border rounded mb-3">
      <div className="text-sm text-gray-700 mb-2">Quick order lookup — reduce support calls.</div>

      <div className="flex gap-2 mb-2">
        <input
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          placeholder="Order # (e.g., #1234)"
          className="border p-2 rounded flex-grow"
        />
        <button
          onClick={() => lookup({ order_number: orderNumber })}
          disabled={loading || !orderNumber}
          className="px-3 py-2 bg-indigo-600 text-white rounded"
        >
          {loading ? 'Checking…' : 'Check'}
        </button>
      </div>

      <div className="text-xs text-gray-500 mb-2">Or lookup recent orders by email</div>

      <div className="flex gap-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email on order"
          className="border p-2 rounded flex-grow"
        />
        <button
          onClick={() => lookup({ email })}
          disabled={loading || !email}
          className="px-3 py-2 border rounded"
        >
          {loading ? 'Checking…' : 'Lookup'}
        </button>
      </div>

      {msg && <div className="text-xs text-red-600 mt-2">{msg}</div>}
    </div>
  );
}

export default function ChatWidget() {
  const globalCfg = getGlobalWidgetConfig();

  const cfg = useMemo(() => {
    const merged = {
      ...DEFAULTS,
      ...globalCfg,
      ui: {
        ...DEFAULTS.ui,
        ...(globalCfg.ui || {}),
      },
    };

    merged.apiBase = (merged.apiBase || '').replace(/\/$/, '');
    merged.tenantId = merged.tenantId || DEFAULTS.tenantId;
    merged.assistantType = merged.assistantType || merged.tenantId || DEFAULTS.assistantType;
    merged.namespace = merged.namespace || merged.tenantId || DEFAULTS.namespace;

    // Keep the public ScalableDeploy site assistant on the clean v2 knowledge namespace.
    // tenantId and assistantType remain 'scalabledeploy-site' for tenant and prompt lookup.
    if (isInternalTenant(merged.tenantId)) {
      merged.namespace = 'scalabledeploy-site-v2';
    }

    merged.ui.nudgeDelayMs = clamp(Number(merged.ui.nudgeDelayMs || 0), 0, 600000);
    merged.ui.nudgeOnScrollPercent = clamp(Number(merged.ui.nudgeOnScrollPercent ?? 0), 0, 100);

    const explicitEnableNudge = typeof globalCfg?.ui?.enableNudge === 'boolean' ? globalCfg.ui.enableNudge : undefined;

    if (isInternalTenant(merged.tenantId)) {
      merged.ui.enableNudge = explicitEnableNudge ?? false;
    } else {
      merged.ui.enableNudge = explicitEnableNudge ?? merged.ui.enableNudge;
    }

    return merged;
  }, [globalCfg]);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: 'welcome', who: 'bot', text: cfg.ui.welcomeMessage }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const [showNudge, setShowNudge] = useState(false);
  const [showOrderLookup, setShowOrderLookup] = useState(false);
  const [pendingOrderQuery, setPendingOrderQuery] = useState('');

  const nudgeShownRef = useRef(false);
  const scrollListenerRef = useRef(null);
  const timerRef = useRef(null);
  const sessionIdRef = useRef(null);
  const firstSendRef = useRef(false);

  const quickPrompts = useMemo(() => {
    if (isInternalTenant(cfg.tenantId)) {
      return [
        'What are your current plans and prices?',
        'What is included in Growth?',
        'How does the 7-day trial work?',
        'Can you work with WooCommerce?',
      ];
    }

    return [
      'Where is my order?',
      'What size should I buy?',
      'What are your ingredients?',
      'Can I see the demo?',
    ];
  }, [cfg.tenantId]);

  async function emitEvent(type, extra = {}) {
    try {
      if (!sessionIdRef.current) sessionIdRef.current = getSessionId(cfg.tenantId);

      await axios.post(`${cfg.apiBase}/widget/event`, {
        tenantId: cfg.tenantId,
        event: type,
        ts: Date.now(),
        page: getPagePath(),
        sessionId: sessionIdRef.current,
        ...extra,
      });
    } catch {
      // non-blocking telemetry
    }
  }

  useEffect(() => {
    setMessages([{ id: 'welcome', who: 'bot', text: cfg.ui.welcomeMessage }]);
  }, [cfg.ui.welcomeMessage]);

  useEffect(() => {
    const key = `sd_widget_nudge_shown_${cfg.tenantId}`;
    const already =
      cfg.ui.nudgeOncePerSession && typeof window !== 'undefined'
        ? sessionStorage.getItem(key) === '1'
        : false;

    nudgeShownRef.current = already;

    function markShown() {
      if (nudgeShownRef.current) return;

      nudgeShownRef.current = true;

      if (cfg.ui.nudgeOncePerSession) {
        try {
          sessionStorage.setItem(key, '1');
        } catch {
          // ignore
        }
      }

      setShowNudge(true);
    }

    function maybeShowOnScroll() {
      if (!cfg.ui.enableNudge) return;
      if (open) return;
      if (nudgeShownRef.current) return;

      const pct = cfg.ui.nudgeOnScrollPercent;
      if (!pct || pct <= 0) return;

      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const scrolled = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      if (scrolled >= pct) markShown();
    }

    function setup() {
      if (timerRef.current) clearTimeout(timerRef.current);

      if (scrollListenerRef.current) {
        window.removeEventListener('scroll', scrollListenerRef.current);
        scrollListenerRef.current = null;
      }

      setShowNudge(false);

      if (!cfg.ui.enableNudge) return;
      if (open) return;
      if (nudgeShownRef.current) return;

      if (cfg.ui.nudgeDelayMs > 0) {
        timerRef.current = setTimeout(() => {
          if (!open && !nudgeShownRef.current) {
            markShown();
          }
        }, cfg.ui.nudgeDelayMs);
      }

      if (cfg.ui.nudgeOnScrollPercent > 0) {
        scrollListenerRef.current = () => maybeShowOnScroll();
        window.addEventListener('scroll', scrollListenerRef.current, { passive: true });
        maybeShowOnScroll();
      }
    }

    setup();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      if (scrollListenerRef.current) {
        window.removeEventListener('scroll', scrollListenerRef.current);
        scrollListenerRef.current = null;
      }
    };
  }, [
    cfg.tenantId,
    cfg.ui.enableNudge,
    cfg.ui.nudgeDelayMs,
    cfg.ui.nudgeOncePerSession,
    cfg.ui.nudgeOnScrollPercent,
    open,
  ]);

  function hideNudge() {
    setShowNudge(false);
  }

  async function callRag(queryText, extra = {}) {
    emitEvent('rag_query_called', { source: extra.source || 'chat' });

    try {
      const res = await axios.post(`${cfg.apiBase}/rag/query`, {
        query: queryText,
        tenantId: cfg.tenantId,
        assistantType: cfg.assistantType,
        namespace: cfg.namespace,
        page: getPagePath(),
        sessionId: sessionIdRef.current || getSessionId(cfg.tenantId),
      });

      const { answer, sources } = res.data || {};

      setMessages((m) => [
        ...m,
        {
          id: Date.now() + '-bot',
          who: 'bot',
          text: answer || "I couldn't find that in the docs yet.",
          sources: sources || [],
        },
      ]);
    } catch (err) {
      console.error('widget query error', err?.response?.data || err);

      setMessages((m) => [
        ...m,
        {
          id: Date.now() + '-err',
          who: 'bot',
          text: 'Something went wrong reaching the assistant. Please try again.',
        },
      ]);
    }
  }

  async function sendQuery(e) {
    if (e) e.preventDefault();

    const q = input.trim();
    if (!q || loading) return;

    setMessages((m) => [...m, { id: Date.now().toString(), who: 'you', text: q }]);
    setInput('');

    emitEvent('message_send');

    if (!firstSendRef.current) {
      firstSendRef.current = true;
      emitEvent('first_message_send');
    }

    const shouldTryOrder = !isInternalTenant(cfg.tenantId) && looksLikeOrderQuery(q);

    if (shouldTryOrder) {
      setPendingOrderQuery(q);
      setShowOrderLookup(true);
      emitEvent('order_lookup_prompt', { triggeredBy: 'message_detect' });
      return;
    }

    setLoading(true);

    try {
      await callRag(q, { source: 'chat' });
    } finally {
      setLoading(false);
    }
  }

  function onToggle() {
    setOpen((v) => {
      const next = !v;

      if (next) {
        hideNudge();
        emitEvent('widget_open');
      } else {
        emitEvent('widget_close');
      }

      return next;
    });
  }

  function handleOrderResult(orderData) {
    const parts = [];

    if (orderData.order_number) parts.push(`Order ${orderData.order_number}`);
    if (orderData.fulfillment_status) parts.push(`status: ${orderData.fulfillment_status}`);

    const text = parts.join(' — ');

    setMessages((m) => [...m, { id: Date.now() + '-order', who: 'bot', text }]);
    setShowOrderLookup(false);
  }

  function handleOrderNoMatch() {
    setShowOrderLookup(false);

    const q = pendingOrderQuery;
    setPendingOrderQuery('');

    if (q) {
      setLoading(true);
      callRag(q, { source: 'order_no_match' }).finally(() => setLoading(false));
    } else {
      setMessages((m) => [
        ...m,
        {
          id: Date.now() + '-nomatch',
          who: 'bot',
          text: "I couldn't find an order — try a product question, or check the order number/email.",
        },
      ]);
    }
  }

  const startTrialHref = '/trial';
  const demoHref = '/demo/rag';

  return (
    <>
      {showNudge && !open && (
        <div className="fixed bottom-16 right-4 z-40 w-72 sm:w-80">
          <div className="bg-white border rounded-xl shadow-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm text-gray-800 leading-snug">{cfg.ui.nudgeText}</div>
              <button
                type="button"
                onClick={hideNudge}
                className="text-xs text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  hideNudge();
                  setOpen(true);
                  emitEvent('widget_open', { location: 'nudge' });
                }}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700"
              >
                Ask a question
              </button>

              <a
                href={startTrialHref}
                onClick={() => emitEvent('cta_click', { cta: 'trial', location: 'nudge' })}
                className="px-3 py-1.5 border rounded text-xs text-gray-800 hover:shadow"
              >
                Start trial
              </a>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onToggle}
        className="fixed bottom-4 right-4 z-40 rounded-full shadow-lg px-4 py-2 bg-indigo-600 text-white text-sm flex items-center gap-2 hover:bg-indigo-700"
        aria-label="Open ScalableDeploy chat widget"
      >
        <span>{cfg.ui.bubbleLabel}</span>
        {cfg.ui.bubbleSubLabel ? (
          <span className="hidden sm:inline text-xs opacity-80">{cfg.ui.bubbleSubLabel}</span>
        ) : null}
      </button>

      {open && (
        <div className="fixed bottom-16 right-4 z-40 w-80 sm:w-96 bg-white border rounded-xl shadow-2xl flex flex-col overflow-hidden">
          <header className="px-3 py-2 bg-gray-900 text-white flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide opacity-80">{cfg.ui.headerKicker}</div>
              <div className="text-sm font-semibold">{cfg.ui.headerTitle}</div>
            </div>

            <div className="flex items-center gap-2">
              {!isInternalTenant(cfg.tenantId) && (
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderLookup((s) => !s);
                    emitEvent('order_lookup_toggle', { visible: !showOrderLookup });
                  }}
                  className="text-xs px-2 py-1 border rounded bg-gray-800 text-white"
                >
                  Check order
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  emitEvent('widget_close');
                }}
                className="text-xs opacity-80 hover:opacity-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </header>

          <div className="px-3 pt-3 pb-2 flex flex-wrap gap-2 border-b bg-white">
            {quickPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => {
                  setInput(prompt);
                  emitEvent('quick_prompt_click', { prompt });
                }}
                className="text-xs px-2 py-1 rounded-full border bg-gray-50 hover:bg-gray-100"
              >
                {prompt}
              </button>
            ))}
          </div>

          <div className="flex-1 p-3 space-y-2 overflow-y-auto text-sm" style={{ maxHeight: '320px' }}>
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.who === 'you' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`px-3 py-2 rounded-lg max-w-[80%] ${
                    m.who === 'you' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'
                  }`}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {m.text}

                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      <strong>Sources:</strong>
                      <ul className="mt-1 space-y-1">
                        {m.sources.map((s) => {
                          const safeInternalHref = isInternalTenant(cfg.tenantId) ? getInternalSourceHref(s.url) : null;
                          const hasValidUrl = isInternalTenant(cfg.tenantId) ? Boolean(safeInternalHref) : Boolean(s.url && s.url !== '#');

                          return (
                            <li key={s.id}>
                              {hasValidUrl ? (
                                <a
                                  href={isInternalTenant(cfg.tenantId) ? safeInternalHref : s.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-indigo-600 hover:underline"
                                >
                                  {s.title || s.id}
                                </a>
                              ) : (
                                <span>{s.title || s.id}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {showOrderLookup && !isInternalTenant(cfg.tenantId) && (
              <OrderLookup
                apiBase={cfg.apiBase}
                onResult={handleOrderResult}
                onNoMatch={handleOrderNoMatch}
                emitEvent={emitEvent}
              />
            )}

            {loading && <div className="text-xs text-gray-500">Thinking…</div>}
          </div>

          <form onSubmit={sendQuery} className="border-t px-2 py-2 bg-gray-50 flex items-center gap-2">
            <input
              className="flex-1 text-sm border rounded px-2 py-1"
              placeholder={cfg.ui.placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendQuery();
                }
              }}
            />

            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="text-xs px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-60 hover:bg-indigo-700"
            >
              Send
            </button>
          </form>

          <div className="px-3 py-2 border-t bg-white flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap gap-2">
              <a
                href={startTrialHref}
                onClick={() => emitEvent('cta_click', { cta: 'trial', location: 'widget' })}
                className="text-xs px-3 py-1 rounded border hover:shadow"
              >
                Start trial
              </a>

              <a
                href={demoHref}
                onClick={() => emitEvent('cta_click', { cta: 'demo', location: 'widget' })}
                className="text-xs px-3 py-1 rounded border hover:shadow"
              >
                View demo
              </a>
            </div>

            <button
              type="button"
              onClick={() => {
                setMessages((m) => [
                  ...m,
                  {
                    id: Date.now() + '-hint',
                    who: 'bot',
                    text: isInternalTenant(cfg.tenantId)
                      ? 'You can ask about current plan pricing, the 7-day trial, setup timing, platform fit, or supported integrations.'
                      : 'You can ask about orders, products, shipping, ingredients, or product fit.',
                  },
                ]);
              }}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              Tips
            </button>
          </div>

          <div className="px-3 py-1 bg-gray-900 text-[10px] text-gray-300 text-right">
            {cfg.ui.footerText}
          </div>
        </div>
      )}
    </>
  );
}